import { useEffect } from 'react';
import { resolveAiSource } from '@/lib/aiTraffic';
import { resolveAcquisition } from '@/lib/acquisition';
import { setTag, trackEvent } from '@/lib/analytics';

/**
 * Tags the Clarity session with the AI assistant that sent this visitor, if any.
 *
 * Without this the channel is unmeasurable: AI engines link with `noreferrer`,
 * so Clarity records almost every session as having no referrer, and only
 * ChatGPT identifies itself via `utm_source`. Tagging lets the whole AI channel
 * be segmented and filtered in the Clarity dashboard.
 *
 * Safe to call before Clarity's script has downloaded — its snippet installs a
 * synchronous queueing stub in <head>, so early calls are buffered rather than
 * lost. That matters here, because the script itself is deferred to idle.
 */
export function AiTrafficTag() {
  useEffect(() => {
    // Capture first-touch acquisition on the LANDING page, not at signup.
    // resolveAcquisition() persists on first call, so calling it here is what
    // makes the stored value the original source. Reading it lazily at signup
    // instead would see document.referrer from the previous in-site page and
    // record almost everyone as "direct" — losing the entire point.
    resolveAcquisition();

    const source = resolveAiSource();
    if (!source) return;

    setTag('ai_source', source.id);
    setTag('ai_source_via', source.via);
    // A coarse tag makes "all AI traffic" filterable in one click, without
    // having to select every engine individually.
    setTag('traffic_type', 'ai_assistant');
    trackEvent(`ai_referral_${source.id}`);
    // `upgrade` has no wrapper: it prioritises this session for recording, which
    // matters because AI traffic is the segment we most want to watch and it is
    // a small share of sessions — exactly what Clarity's sampling would drop.
    try {
      window.clarity?.('upgrade', 'ai_assistant_referral');
    } catch {
      // Analytics must never break the page.
    }
  }, []);

  return null;
}
